import { useMutation } from "react-query";

import { useGetFileByHashLazyQuery } from "~/schemas/generated/graphql";

import { useCombinationRules } from "./useCombinationRules";

interface MutateOptions {
  onSuccess?: (data?: any) => void;
  onError?: (error: any) => void;
}

interface DataIProps {
  responseType?: "blob" | "text";
  options?: MutateOptions;
}

const DEFAULT_FILE_NAME = "Отчет о совмещениях.xlsx";

export const useDownloadCombinationRulesReport = () => {
  const { rulesReportGet } = useCombinationRules();
  const [getFileByHash] = useGetFileByHashLazyQuery({
    fetchPolicy: "no-cache",
    async onCompleted(data) {
      const file = data?.FileGetter?.getFileByHash;
      if (file?.link) {
        const response = await fetch(file.link);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", DEFAULT_FILE_NAME);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    },
  });

  const mutation = useMutation({
    mutationFn: async (props: DataIProps) => {
      const responseType = props.responseType ?? "text";
      const response = await rulesReportGet(responseType);

      if (responseType === "blob") {
        const contentDisposition = response.headers["content-disposition"];
        const fileName = contentDisposition
          ? decodeURIComponent(
              contentDisposition.split("filename=")[1].replace(/"/g, ""),
            )
          : DEFAULT_FILE_NAME;

        const url = window.URL.createObjectURL(
          new Blob([response.data as Blob], {
            type: String(response.headers["content-type"]),
          }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        const text = String(response.data);
        let fileHash: string | undefined;
        try {
          fileHash = JSON.parse(text)?.fileHash;
        } catch {
          fileHash = undefined;
        }
        if (fileHash) {
          await getFileByHash({ variables: { hash: fileHash } });
        }
      }

      return props;
    },
    onSuccess: (data, { options }) => {
      options?.onSuccess?.(data);
    },
    onError: (error, { options }) => {
      options?.onError?.(error);
    },
  });

  const download = (
    options?: MutateOptions & { responseType?: "blob" | "text" },
  ) => {
    mutation.mutate({
      responseType: options?.responseType ?? "text",
      options,
    });
  };

  return { download, isLoading: mutation.isLoading };
};
